import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import {
	AbstractModal,
	ModalButtonModel,
	ModalButtonType, ObjectUtils,
	ToasterType
} from '@cv-invision/core';

import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

// Models
import { FieldTypes, FormFieldModel, FormModelService } from '@cv-invision/cv.invision-views.ux';

// Services
import { BroadcastService } from '@cv-invision/core'
import { GenericFormService } from '@cv-invision/cv.invision-views.ux';


@Component({
	selector: 'cv-create-update-generic-crud',
	templateUrl: './create-update-form.component.html',
	styleUrls: [ './create-update-form.component.scss' ],
})

export class CreateUpdateFormComponent extends AbstractModal implements OnInit, OnDestroy {

	private atComponentDestroy = new Subject();
	private label: any;
	private integrationExtraData: any;
	public integrationParams: any;
	private isEditing: boolean;
	public modelData: any;
	public formBuilder: any = {};
	public formBuilderConfig: any;
	public formFields: FormFieldModel[] = []
	public saving: boolean;
	public showFields = false;

	constructor(
		private broadcastService: BroadcastService,
		private formService: GenericFormService,
		private formModelService: FormModelService
	) {
		super();
		this.saving = false;
	}

	async ngOnInit(): Promise<void> {
		this.isEditing = false;
		if (this.data.formBuilderConfig) {
			this.formBuilderConfig = ObjectUtils.recursiveDeepCopy(this.data.formBuilderConfig);
			this.integrationExtraData = ObjectUtils.recursiveDeepCopy(this.data.integrationExtraData);
			this.integrationParams = ObjectUtils.recursiveDeepCopy(this.data.integrationParams);
			this.label = ObjectUtils.recursiveDeepCopy(this.data.label);
			if (this.integrationParams.section === 'organizations') {
				this.formBuilderConfig[ 'classOrganization' ].UIOptions.Url = `${ this.integrationExtraData.rootUrl }classvaluation/organizations`
			}

			if (this.data.isEditing) {
				this.isEditing = this.data.isEditing;
				this.modelData = this.data.modelData;
			}
		}

		const cancelButton: ModalButtonModel = {
			name: 'close',
			text: 'Close',
			class: 'btn-sm',
			type: ModalButtonType.ACTION,
			action: this.onCancel.bind(this)
		};
		const confirmButton: ModalButtonModel = {
			name: 'save',
			text: 'Save',
			class: 'btn-sm cv-btn-blue ',
			type: ModalButtonType.ACTION,
			disabled: (): boolean => !this.formService.form?.valid || this.saving,
			action: () => this.onSave(),
			loading: (): boolean => this.saving,
			loadingText: 'Saving'
		};

		this.buttons.push(cancelButton);
		this.buttons.push(confirmButton);
		this.formBuilder = this.createForm(this.formBuilder, this.formBuilderConfig, this.modelData);
		this.formService.setFormFieldList(this.formBuilderConfig);
		await this.formService.initForm(this.formBuilder);
		this.formService.formObserver.subscribe((data) => {
			this.showFields = true;
			this.renderFields(this.formBuilder, this.formFields, this.formBuilderConfig);
		})

		this.formService.onChangeOptions$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: any) => {
				const field = data.field;
				const fieldOptions = data.fieldOptions;
				if (field.UIEvents.onChange) {
					field.UIEvents.onChange(data.event, this.formBuilderConfig, this.formService.form, this.integrationExtraData, fieldOptions);
				}
			})

		this.formService.onSelectOptions$
			.pipe(takeUntil(this.atComponentDestroy))
			.subscribe((data: any) => {
				const field = data.field;
				if (field.UIEvents.onSelect) {
					field.UIEvents.onSelect(data.event, this.formService.form);
				}
			})
	}

	createForm(formBuilder: any, formBuilderConfig: any, modelData: any, path: any = []): any {
		for (const key in formBuilderConfig) {
			let value: any;
			if (key !== 'DataType') {
				const rootPath = [ ...path, `${ key }` ];
				const formField = this.formModelService.getValue(this.formBuilderConfig, rootPath.join('.'));

				if ('DataType' in formField && formField.DataType === 'Object' && !this.isEditing) {
					if (!formBuilder[ key ]) {
						formBuilder[ key ] = {}
					}
					this.createForm(formBuilder[ key ], formField, modelData, rootPath);
				} else {
					if (formField.DataType === 'Array') {
						value = [];
					}

					if ('defaultValue' in formField) {
						value = formField[ 'defaultValue' ];
					}

					if (this.isEditing) {
						if ('UIControl' in formField && formField.UIControl === FieldTypes.AUTOCOMPLETE) {
							const formFieldOptions = formField.UIOptions;
							if (modelData[ formFieldOptions.Mapper.id ] && modelData[ formFieldOptions.Mapper.name ]) {
								value = {
									id: modelData[ formFieldOptions.Mapper.id ],
									name: modelData[ formFieldOptions.Mapper.name ]
								};
							}
						} else {
							value = modelData[ key ];
							if ('UIOptions' in formField && 'Mapper' in formField.UIOptions) {
								const formFieldOptions = formField.UIOptions;
								if (modelData[ formFieldOptions.Mapper.id ] && modelData[ formFieldOptions.Mapper.name ]) {
									value = {
										id: modelData[ formFieldOptions.Mapper.id ],
										name: modelData[ formFieldOptions.Mapper.name ]
									};
								}
							}
							if (formField.DataType === 'Array') {
								formField.extraData = this.integrationExtraData;
							}
						}
						if ('UIEvents' in formField && formField.UIEvents.onChange && value != '') {
							formField.UIEvents.onChange(
								{ value: value }, this.formBuilderConfig, this.formService.form, this.integrationExtraData);
						}
					}
					formBuilder[ key ] = value;
				}
			}
		}
		return formBuilder;
	}

	/**
	 * Event to call when form is created to render in the view
	 * @param formBuilder
	 * @param formFields
	 * @param formBuilderConfig
	 */
	renderFields(formBuilder: any, formFields: any, formBuilderConfig: any, path: any = []): void {
		for (const key in formBuilder) {
			const rootPath = [ ...path, `${ key }` ]
			const formFieldConfig = this.formModelService.getValue(this.formBuilderConfig, rootPath.join('.'));

			if ('DataType' in formFieldConfig && formFieldConfig.DataType === 'Object') {
				this.renderFields(formBuilder[ key ], formFields, formBuilderConfig, rootPath);
			} else {
				const field = {
					key: key,
					path: rootPath.join('.'),
					label: formFieldConfig.DisplayText,
					hidden: formFieldConfig.hidden === undefined ? false : formFieldConfig.hidden,
				}
				formFields.push(field);
			}
		}
	}

	/**
	 * On Save button.
	 */
	onSave(): void {
		const values = this.formService.form.value;
		this.formService.submitObserver.next({
			submit: true,
			data: values,
			isEditing: this.isEditing
		});
		this.onDismiss();
	}

	/**
	 * handle response from server
	 */
	successHandleFunction(): void {
		const msgSuget = this.data.isEditing ? 'Updated' : 'Created';
		const msg = `The Lender Company Message Configuration Was ${ msgSuget } Successfully`;
		this.broadcastService.broadcast({
			name: ToasterType.SUCCESS,
			event: { message: msg }
		});
	}

	/**
	 * Display error message if the operation was not success
	 * @param error
	 */
	errorMsgFunction(error: string): void {
		this.broadcastService.broadcast({
			name: ToasterType.ERROR,
			event: { error }
		});
	}

	/**
	 * Event to close modal
	 */
	onDismiss(): void {
		super.onCancel();
	}

	/**
	 * Event is called when component is destroyed
	 */
	ngOnDestroy(): void {
		this.atComponentDestroy.next({});
		this.atComponentDestroy.unsubscribe();
	}
}
